<template>
  <div class="ag-risco-legenda">
    <div v-for="item in items" :key="item" :class="['ag-risco-legenda__item', getClass(item)]">
      <div class="ag-risco-legenda__item-title">
        {{ getDescriptionForGrauRisco(item) }}

        <AGTooltip>
          <template #activator="{ props }">
            <AGIcon v-bind="props" icon="md:help" size="14px" />
          </template>

          {{ getTooltipForGrauRisco(item) }}
        </AGTooltip>
      </div>
      <div class="ag-risco-legenda__item-description">
        {{ getSummaryForGrauRisco(item) }}
      </div>
    </div>
  </div>
</template>

<script>
import AGIcon from '@/components/common/AGIcon.vue';
import AGTooltip from '@/components/common/AGTooltip.vue';
import NIVEL_RISCO from '@/constants/ConstantNivelRisco';
import UtilsAnalise from '@/utils/UtilsAnalise';

export default {
  components: { AGIcon, AGTooltip },
  data: () => ({
    items: [
      NIVEL_RISCO.INEXPRESSIVO,
      NIVEL_RISCO.BAIXO,
      NIVEL_RISCO.MODERADO,
      NIVEL_RISCO.ELEVADO,
      NIVEL_RISCO.CRITICO,
    ],
  }),
  methods: {
    getDescriptionForGrauRisco: UtilsAnalise.getDescriptionForGrauRisco,

    getClass(grauRisco) {
      return `ag-risco-legenda__item--${grauRisco}`;
    },
    getSummaryForGrauRisco(grauRisco) {
      switch (grauRisco) {
        case NIVEL_RISCO.CRITICO:
          return `Risco de exposição imediata por meio do cruzamento automatizado de dados enviados ao eSocial e demais
          obrigações acessórias.`;
        case NIVEL_RISCO.ELEVADO:
          return `Risco com potencial de materialização a partir de fiscalizações ou auditorias (internas ou externas),
          que exigem a análise de documentos eletrônicos e físicos.`;
        case NIVEL_RISCO.MODERADO:
          return `Risco de questionamento individualizado, com potencial de materialização por meio de ações judiciais
          trabalhistas, denúncias formais ou fiscalizações pontuais motivadas por indícios ou reclamações específicas.`;
        case NIVEL_RISCO.BAIXO:
          return `Risco com baixa probabilidade de materialização, mas que pode indicar oportunidades de melhoria nos
          processos internos de registro e envio de eventos do eSocial.`;
        case NIVEL_RISCO.INEXPRESSIVO:
          return `Situação plenamente em conformidade com os requisitos legais e operacionais do eSocial, sem indícios
          de falhas ou inconsistências nos registros e envios.`;
      }
    },
    getTooltipForGrauRisco(grauRisco) {
      switch (grauRisco) {
        case NIVEL_RISCO.CRITICO:
          return `Indica potencial descumprimento da legislação trabalhista ou previdenciária, podendo acarretar em
          omissão ou erro em informações obrigatórias, geração automática de passivos tributários e multas e
          identificação automática pelos sistemas da Receita Federal, INSS ou Ministério do Trabalho.`;
        case NIVEL_RISCO.ELEVADO:
          return `Demonstra falha nos registros eletrônicos, com alto potencial de responsabilização. Possibilidade de
          autuações, termos de ajustamento de conduta (TAC) e multas administrativas.`;
        case NIVEL_RISCO.MODERADO:
          return `Trata-se de situações em que a irregularidade não é detectada automaticamente pelos sistemas
          governamentais, mas que pode gerar impacto financeiro e reputacional relevante caso confirmada.`;
        case NIVEL_RISCO.BAIXO:
          return `Pontos de atenção que não geram impacto direto imediato, mas que podem indicar fragilidades nos
          controles internos. Importante para conformidade e governança, mas sem repercussões legais automáticas.`;
        case NIVEL_RISCO.INEXPRESSIVO:
          return `Possibilidade remota de questionamentos administrativos ou judiciais, restrita a diferenças de
          interpretação da legislação ou entendimento de órgãos fiscalizadores, sem expectativa de impacto financeiro
          ou reputacional.`;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.ag-risco-legenda {
  width: 100%;
  font-size: $font-size-xxxs;
  border-radius: $border-radius-sm;
  overflow: hidden;

  &__item {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;

    padding: 8px;

    text-wrap: auto;
    border-left-width: $border-width-thick;
    border-left-style: $border-default;

    &--INEXPRESSIVO {
      background-color: $neutral-color-low-dark;
      border-left-color: $neutral-color-high-medium;
      color: $neutral-color-high-medium;
    }

    &--BAIXO {
      background-color: $feedback-color-success-light;
      border-left-color: $feedback-color-success-flourescent;
      color: $feedback-color-success-dark;
    }

    &--MODERADO {
      background-color: $feedback-color-attention-light;
      border-left-color: $feedback-color-attention-flourescent;
      color: $feedback-color-attention-dark;
    }

    &--ELEVADO {
      background-color: $feedback-color-more-attention-light;
      border-left-color: $feedback-color-more-attention-flourescent;
      color: $feedback-color-more-attention-dark;
    }

    &--CRITICO {
      background-color: $feedback-color-error-light;
      border-left-color: $feedback-color-error-flourescent;
      color: $feedback-color-error-dark;
    }
  }

  &__item-title {
    font-weight: $font-weight-bold;
    white-space: nowrap;
    min-width: 100px;

    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    gap: 4px;
  }

  &__item-description {
    hyphens: manual;
  }
}
</style>
